import { z } from 'zod';
import { configKey, editableText, entityId, isoDateTime } from './primitives.js';

/**
 * CONTROLE DE ACESSO (Cap. 4, 22 e 24)
 *
 * ┌──────────────────────────────────────────────────────────────────────┐
 * │ AVISO QUE VALE PARA TODO ESTE ARQUIVO                                │
 * │                                                                      │
 * │ Nada aqui autoriza coisa alguma. Este modelo existe para o cliente   │
 * │ saber o que DESENHAR — quais menus mostrar, quais botões habilitar.  │
 * │                                                                      │
 * │ A autorização de verdade é do servidor, em toda requisição, sem      │
 * │ exceção. O navegador é território do usuário: ele pode editar o      │
 * │ estado, forjar a resposta do /bootstrap e chamar a API direto. Uma   │
 * │ tela escondida não protege nada.                                     │
 * │                                                                      │
 * │ Regra prática: se esconder o botão for a única barreira, não há      │
 * │ barreira.                                                            │
 * └──────────────────────────────────────────────────────────────────────┘
 */

/**
 * Uma permissão nomeia UMA capacidade, no formato `recurso.acao`.
 *
 * Granularidade por ação, e não por tela, é o que permite montar perfis que
 * o documento pede e que não caberiam num enum de papéis: o moderador aprova
 * anúncio mas não vê pagamento; o financeiro vê pagamento mas não aprova
 * anúncio (Cap. 4.2 e 4.3).
 */
export const permission = z.object({
  key: z
    .string()
    .min(3)
    .max(64)
    .regex(/^[a-z][a-z0-9_]*\.[a-z][a-z0-9_]*$/, 'Use o formato recurso.acao (ex.: listings.approve).'),
  group: configKey,
  label: editableText,
  description: editableText.nullable().default(null),

  /**
   * Ação de alto impacto: apagar conta, estornar pagamento, alterar
   * permissões. A interface avisa antes, o servidor exige reautenticação e o
   * registro de auditoria é obrigatório.
   */
  sensitive: z.boolean().default(false),

  /**
   * Exige reautenticação recente (senha ou 2FA) mesmo com sessão aberta.
   * Cobre o Cap. 24: sessão esquecida aberta não pode virar chave-mestra.
   */
  requiresStepUp: z.boolean().default(false),

  /**
   * Dá acesso a dado pessoal (CPF, telefone, endereço). Separado de
   * `sensitive` porque o risco é outro: aqui é LGPD (Cap. 25), e o acesso
   * é registrado mesmo quando é só leitura.
   */
  exposesPersonalData: z.boolean().default(false),
});

export type Permission = z.infer<typeof permission>;

/**
 * Perfil interno (Cap. 4). É um conjunto de permissões com nome — não um
 * valor fixo no código.
 *
 * Criar o perfil "Supervisor de moderação", que o documento não previu, é
 * trabalho de administrador.
 */
export const role = z.object({
  key: configKey,
  name: editableText,
  description: editableText.nullable().default(null),
  permissions: z.array(z.string()).default([]),

  /**
   * Altura do perfil na hierarquia. Serve a uma regra de segurança concreta:
   * ninguém edita, atribui ou apaga um perfil de nível igual ou maior que o
   * seu. Sem isso, um administrador comum se promove a master editando o
   * próprio perfil.
   */
  level: z.number().int().min(0).max(100).default(10),

  /**
   * Perfil de sistema não pode ser apagado nem ter a chave alterada. Apagar
   * o `admin_master` deixaria a plataforma sem ninguém capaz de recriá-lo.
   */
  isSystem: z.boolean().default(false),

  /** Interno = perfil de equipe (Cap. 4). Externo = anunciante, comprador. */
  scope: z.enum(['internal', 'external']).default('internal'),
});

export type Role = z.infer<typeof role>;

export const accessConfig = z.object({
  version: z.string(),
  permissions: z.array(permission).default([]),
  roles: z.array(role).default([]),

  /**
   * Minutos desde a última confirmação de identidade para uma ação sensível
   * ser aceita sem pedir de novo.
   */
  stepUpWindowMinutes: z.number().int().min(1).max(120).default(15),

  /** 2FA obrigatório para perfis a partir deste nível (Cap. 24). */
  requireTwoFactorFromLevel: z.number().int().min(0).max(100).default(50),
});

export type AccessConfig = z.infer<typeof accessConfig>;

// ------------------------------------------------------------- usuários

export const adminUserStatus = z.enum(['active', 'invited', 'suspended', 'disabled']);

/**
 * Usuário interno como o painel o exibe.
 *
 * Repare no que NÃO está aqui: nada de hash de senha, segredo de 2FA ou
 * token de sessão. Esses dados nunca saem do servidor, nem para o painel do
 * administrador master.
 */
export const adminUser = z.object({
  id: entityId,
  name: z.string(),
  email: z.string(),
  roleKey: configKey,
  status: adminUserStatus,
  twoFactorEnabled: z.boolean().default(false),
  lastAccessAt: isoDateTime.nullable().default(null),
  createdAt: isoDateTime,

  /**
   * Efetivas, já resolvidas pelo SERVIDOR a partir do perfil. O cliente não
   * calcula permissão a partir do nome do papel: se um dia a regra de
   * composição mudar, ela muda num lugar só — e do lado que manda.
   */
  permissions: z.array(z.string()).default([]),
});

export type AdminUser = z.infer<typeof adminUser>;

// ------------------------------------------------------------- auditoria

/**
 * Registro de auditoria (Cap. 24, item "logs").
 *
 * Append-only por natureza: não existe editar nem apagar, nem para o
 * administrador master. Um log que o próprio suspeito pode apagar não serve
 * como log.
 */
export const auditEntry = z.object({
  id: entityId,
  at: isoDateTime,
  actorId: entityId,
  actorName: z.string(),
  actorRole: configKey,

  /** Verbo do que aconteceu: `listing.approved`, `role.permissions_changed`. */
  action: z.string(),

  targetType: z.string(),
  targetId: z.string().nullable().default(null),
  targetLabel: z.string().nullable().default(null),

  /** Antes e depois, quando faz sentido. Sem PII em claro. */
  changes: z.record(z.string(), z.object({ from: z.unknown(), to: z.unknown() })).default({}),

  ip: z.string().nullable().default(null),
  userAgent: z.string().nullable().default(null),

  /** Correlaciona com o rastreamento distribuído do backend. */
  traceId: z.string().nullable().default(null),

  severity: z.enum(['info', 'notice', 'warning', 'critical']).default('info'),
});

export type AuditEntry = z.infer<typeof auditEntry>;

// -------------------------------------------------------------- helpers

/**
 * Avalia uma permissão contra a lista efetiva.
 *
 * Nega por padrão: o que não está explicitamente concedido é negado. E
 * aceita curinga por recurso (`listings.*`) porque o perfil master precisa
 * continuar coerente quando alguém acrescentar uma ação nova ao sistema —
 * do contrário, cada permissão nova nasceria invisível até alguém lembrar
 * de marcá-la.
 */
export function hasPermission(granted: readonly string[], required: string): boolean {
  if (granted.includes('*')) return true;
  if (granted.includes(required)) return true;

  const separator = required.indexOf('.');
  if (separator <= 0) return false;

  return granted.includes(`${required.slice(0, separator)}.*`);
}

export function hasAllPermissions(granted: readonly string[], required: readonly string[]): boolean {
  return required.every((candidate) => hasPermission(granted, candidate));
}

export function hasAnyPermission(granted: readonly string[], required: readonly string[]): boolean {
  return required.some((candidate) => hasPermission(granted, candidate));
}

/**
 * Impede escalada de privilégio na atribuição.
 *
 * Duas regras, e as duas existem por um cenário concreto:
 *
 * 1. ninguém concede o que não tem — senão um corretor se daria acesso ao
 *    financeiro;
 * 2. ninguém mexe em perfil de nível igual ou maior que o seu — senão um
 *    administrador editaria o perfil master, ou o próprio, e viraria master.
 *
 * O servidor refaz esta checagem. Aqui ela serve para a interface não
 * oferecer o que vai ser recusado.
 */
export function canAssignRole(actor: { permissions: readonly string[]; roleLevel: number }, target: Role): boolean {
  if (!hasPermission(actor.permissions, 'users.assign_role')) return false;
  if (target.level >= actor.roleLevel) return false;

  return target.permissions.every((candidate) => hasPermission(actor.permissions, candidate));
}

export function canEditRole(actor: { permissions: readonly string[]; roleLevel: number }, target: Role): boolean {
  if (!hasPermission(actor.permissions, 'roles.edit')) return false;
  // Nem o próprio perfil: quem edita o próprio perfil se autopromove.
  return target.level < actor.roleLevel;
}
